// E8 — approve / reject / cancel of a leave request, behind the confirmation the
// one-click buttons never had. The requests inbox, the employee's own request
// list and the dashboard card all decide through this one hook; the mutation and
// idempotency-key logic used to be copied into each, which is how they drifted.
//
// What the dialog asks is set by what the decision can undo:
//   * reject  — REQUIRES a reason. Without one the requester sees a bare
//     "Reddedildi" and has nothing to act on; the server already accepts it.
//   * approve — a plain confirmation; there is no un-approve on this screen.
//   * cancel  — the reason is optional (it is the requester's own withdrawal).
// A failed decision keeps the dialog open and shows the error inside it, so the
// user retries or backs out of the same context instead of chasing a banner.

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Banner,
  Button,
  FormField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
  Toast,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import {
  approveLeaveRequest,
  cancelLeaveRequest,
  rejectLeaveRequest,
  type LeaveRequestRow,
} from "@/features/leave/leave-api";
import { formatDateRange } from "@/lib/date-range";

export type LeaveDecisionKind = "approve" | "reject" | "cancel";

// Mirrors DecisionBodySchema in services/platform-graph/src/routes/leave.ts;
// the server is the authority, this only stops the textarea accepting a body it
// would answer with a 400.
const REASON_MAX_LENGTH = 2000;

interface Asked {
  readonly kind: LeaveDecisionKind;
  readonly row: LeaveRequestRow;
}

/**
 * A response-loss retry is safe only within the server-authoritative approval
 * step that produced the inbox row, and only for the SAME body: the reason is
 * part of the key, so editing it after a failed attempt is a new request, not a
 * replay the server would reject as a mismatched body. Legacy rows have no
 * multi-step framework context and keep the request+verb fallback.
 */
function decisionKeyId(
  row: LeaveRequestRow,
  kind: "approve" | "reject",
  reason: string,
): string {
  const context = row.decision_context;
  const step =
    context === null || context === undefined
      ? "legacy"
      : `${context.approval_request_id}:${context.current_step}`;
  return `${row.request_id}:${kind}:${step}:${reason}`;
}

export interface LeaveDecisions {
  /** Opens the confirmation for `kind` on `row`; nothing is sent until confirmed. */
  readonly ask: (kind: LeaveDecisionKind, row: LeaveRequestRow) => void;
  /** The dialog + the success toast; render it once, anywhere in the page. */
  readonly layer: React.ReactNode;
}

export function useLeaveDecisions(): LeaveDecisions {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [asked, setAsked] = useState<Asked | null>(null);
  const [reason, setReason] = useState("");
  const [toast, setToast] = useState<{ id: number; kind: LeaveDecisionKind } | null>(null);

  // One key per request + verb + workflow step + reason, held until a confirmed
  // success so a lost response can be retried without a second decision.
  const pendingKeys = useRef<Map<string, string>>(new Map());
  const decisionKey = (row: LeaveRequestRow, kind: "approve" | "reject", text: string): string => {
    const keyId = decisionKeyId(row, kind, text);
    const existing = pendingKeys.current.get(keyId);
    if (existing !== undefined) return existing;
    const key = crypto.randomUUID();
    pendingKeys.current.set(keyId, key);
    return key;
  };

  const decide = useMutation({
    mutationFn: ({ kind, row, text }: Asked & { readonly text: string }) => {
      if (kind === "cancel") {
        return cancelLeaveRequest(row.request_id, text === "" ? undefined : text);
      }
      const key = decisionKey(row, kind, text);
      return kind === "approve"
        ? approveLeaveRequest(row.request_id, key)
        : rejectLeaveRequest(row.request_id, key, text);
    },
    onSuccess: (_result, { kind, row, text }) => {
      if (kind !== "cancel") pendingKeys.current.delete(decisionKeyId(row, kind, text));
      // Balances move with the decision (a reject or cancel gives the held hours
      // back), so both lists are stale, not just the requests.
      void queryClient.invalidateQueries({ queryKey: ["leave", "requests"] });
      void queryClient.invalidateQueries({ queryKey: ["leave", "balances"] });
      setAsked(null);
      setToast({ id: Date.now(), kind });
    },
  });

  const ask = (kind: LeaveDecisionKind, row: LeaveRequestRow): void => {
    decide.reset();
    setReason("");
    setAsked({ kind, row });
  };
  const dismiss = (): void => {
    if (!decide.isPending) setAsked(null);
  };

  const text = reason.trim();
  const reasonMissing = asked?.kind === "reject" && text === "";

  const dialog =
    asked === null ? null : (
      <Modal open onClose={dismiss}>
        <ModalHeader title={t(`leave.decision.${asked.kind}_title`)} />
        <ModalBody className="space-y-4">
          <p className="text-[13.5px] text-[var(--fg-secondary)]">
            {t(`leave.decision.${asked.kind}_body`, {
              name: asked.row.employee_name ?? asked.row.employee_id.slice(0, 8),
              dates: formatDateRange(i18n.language, asked.row.start_date, asked.row.end_date),
            })}
          </p>
          {asked.kind !== "approve" && (
            <FormField
              label={t(`leave.decision.${asked.kind}_reason_label`)}
              required={asked.kind === "reject"}
              hint={asked.kind === "cancel" ? t("leave.decision.reason_optional") : undefined}
            >
              <Textarea
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                value={reason}
                onChange={(event): void => {
                  setReason(event.target.value);
                }}
              />
            </FormField>
          )}
          {decide.isError && (
            <Banner severity="danger" title={t("leave.list.action_error_title")}>
              {errorMessage(decide.error, t)}
            </Banner>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" disabled={decide.isPending} onClick={dismiss}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={asked.kind === "approve" ? "primary" : "danger"}
            disabled={decide.isPending || reasonMissing}
            onClick={(): void => {
              decide.mutate({ ...asked, text });
            }}
          >
            {decide.isPending
              ? t("leave.decision.working")
              : t(`leave.decision.${asked.kind}_confirm`)}
          </Button>
        </ModalFooter>
      </Modal>
    );

  // The component library ships the Toast surface but no provider, and nothing
  // else in customer-web toasts yet, so the region lives here: bottom-right,
  // click-through except for the toast itself. `key` restarts the auto-dismiss
  // timer when a second decision lands before the first toast has gone.
  const notice =
    toast === null ? null : (
      <div className="pointer-events-none fixed bottom-6 right-6 z-[60] w-[min(360px,calc(100vw-48px))]">
        <Toast
          key={toast.id}
          className="pointer-events-auto"
          severity="success"
          title={t(`leave.decision.${toast.kind}_done`)}
          dismissLabel={t("leave.decision.toast_dismiss")}
          onDismiss={(): void => {
            setToast(null);
          }}
        />
      </div>
    );

  return {
    ask,
    layer: (
      <>
        {dialog}
        {notice}
      </>
    ),
  };
}
